import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import {
  ButtonModule,
  DIALOG_DATA,
  DialogRef,
  equals,
  FiltersForm,
  IconModule,
  RouteFiltersService,
  SelectModule,
} from '@hglobal/fin-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';

import {
  ButtonComponent,
  DateRangePickerComponent,
  TitleComponent,
} from '@iw/common';
import { DialogData } from '@iw/core/entities';
import { DictionariesService, IwDialogService } from '@iw/core/services';
import { WithdrawalComponent } from '@iw/withdrawal';

import { OrdersFilters } from '../../models';
import { initialFilters, OrdersHistoryStore } from '../../orders-history.store';

@Component({
  selector: 'iw-orders-filters',
  imports: [
    IconModule,
    ReactiveFormsModule,
    SelectModule,
    TitleComponent,
    TranslateModule,
    ButtonComponent,
    ButtonModule,
    DateRangePickerComponent,
  ],
  providers: [RouteFiltersService],
  styleUrl: './filters.component.scss',
  templateUrl: './filters.component.html',
  host: { '[class.dialog]': 'isDialogView' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersFiltersComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(OrdersHistoryStore);
  readonly #translate = inject(TranslateService);
  readonly #dialogService = inject(IwDialogService);
  readonly #dictionariesService = inject(DictionariesService);
  readonly #routeFiltersService = inject(RouteFiltersService<OrdersFilters>);
  readonly #dialogRef = inject(DialogRef, { optional: true });
  readonly #dialog = inject<
    DialogData<OrdersFiltersComponent, { isDialogView: boolean }>
  >(DIALOG_DATA, { optional: true });

  readonly filters = this.#store.filters;
  readonly ongoingOrders = this.#store.ongoingOrders;
  readonly accountsCurrencies = this.#store.accountsCurrencies;

  readonly prevMonth = dayjs().startOf('M').subtract(1, 'M').toDate();
  readonly today = new Date();

  isDialogView = this.#dialog?.data?.isDialogView ?? false;

  areFiltersShown = signal(false);
  statuses = this.#dictionariesService.operationFinalStatuses;

  dateRangeControl: FormControl<number[]> = new FormControl<number[]>([
    this.filters().dateFrom,
    this.filters().dateTo,
  ]);

  filtersForm: FormGroup<FiltersForm<OrdersFilters>>;

  get disabled(): boolean {
    return equals(initialFilters, this.filtersForm.getRawValue());
  }

  ngOnInit() {
    this.#initForm();
  }

  applyFilters(): void {
    this.#store.updateFilters(this.filtersForm.getRawValue());

    this.filtersForm.markAsPristine();

    if (this.isDialogView) {
      this.#dialogRef.close();
    }
  }

  toggleFilters(): void {
    this.areFiltersShown.update((shown) => !shown);
  }

  resetFilters(): void {
    this.filtersForm.patchValue({ ...initialFilters });
    this.dateRangeControl.patchValue([
      initialFilters.dateFrom,
      initialFilters.dateTo,
    ]);
    this.applyFilters();
  }

  openWithdrawal(): void {
    this.#dialogService.open<WithdrawalComponent, void>({
      title: this.#translate.instant('withdrawal.dialog.title'),
      components: [WithdrawalComponent],
    });
  }

  #initForm() {
    this.filtersForm = this.#routeFiltersService.initialize({
      filtersState: this.#store.filters,
      updateFilters: this.isDialogView
        ? () => {}
        : (filters: Partial<OrdersFilters>) =>
            this.#store.updateFilters(filters),
      queryParamTypes: [],
      useCustomControlsLogic: true,
    });
    this.#subscribeToDateRangeChanges();
  }

  #subscribeToDateRangeChanges(): void {
    this.dateRangeControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value) => {
        const [dateFrom, dateTo] = value;

        this.filtersForm.patchValue({ dateFrom, dateTo });
        this.filtersForm.markAsDirty();
      });
  }
}
